import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Loading } from "@/components/common";
import { CompanyForm } from "@/components/CompanyForm";
import { LogoUpload } from "@/components/LogoUpload";
import { PriceManager } from "@/components/PriceManager";
import { api, errMsg } from "@/lib/api";

function OwnerFields({ owner, setOwner }) {
  const set = (k) => (e) => setOwner((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="border border-slate-300 rounded-sm p-4 grid sm:grid-cols-3 gap-3">
      <div className="sm:col-span-3 text-xs font-semibold text-slate-700">Akun Pemilik (Owner)</div>
      <div><Label className="text-xs">Username</Label><Input value={owner.owner_username} onChange={set("owner_username")} data-testid="tenant-owner-username" /></div>
      <div><Label className="text-xs">Nama</Label><Input value={owner.owner_name} onChange={set("owner_name")} data-testid="tenant-owner-name" /></div>
      <div><Label className="text-xs">Password</Label><Input type="password" value={owner.owner_password} onChange={set("owner_password")} data-testid="tenant-owner-password" /></div>
    </div>
  );
}

function TenantDialog({ state, onClose, onSaved }) {
  const [owner, setOwner] = useState({ owner_username: "", owner_name: "", owner_password: "" });
  const [t, setT] = useState(null);
  useEffect(() => {
    setT(state.tenant);
    setOwner({ owner_username: "", owner_name: "", owner_password: "" });
  }, [state]);
  const save = async (body) => {
    try {
      if (t) await api.put(`/admin/tenants/${t.id}`, body);
      else await api.post("/admin/tenants", { ...body, ...owner });
      toast.success("Perusahaan disimpan & tercatat di audit log");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={state.open === "edit"} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t ? `Ubah ${t.name}` : "Tambah Perusahaan"}</DialogTitle></DialogHeader>
        {t && <LogoUpload logo={t.logo} uploadUrl={`/admin/tenants/${t.id}/logo`} onUploaded={(logo) => { setT({ ...t, logo }); onSaved(); }} />}
        <CompanyForm initial={state.tenant} onSubmit={save} submitLabel={t ? "Simpan Perubahan" : "Buat Perusahaan"}>
          {!t && <OwnerFields owner={owner} setOwner={setOwner} />}
        </CompanyForm>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTenants() {
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState({ open: null, tenant: null });
  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/tenants")).data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async (t, active) => {
    try { await api.post(`/admin/tenants/${t.id}/status`, { active }); toast.success(`${t.name} ${active ? "diaktifkan" : "dinonaktifkan"}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader sheet="ADMIN 02" title="Manajemen Perusahaan" subtitle="Buat, ubah, aktifkan / nonaktifkan akun perusahaan, logo, kode nota, dan daftar harga.">
        <Button onClick={() => setDlg({ open: "edit", tenant: null })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="tenant-add-btn"><Plus className="h-4 w-4 mr-2" />Tambah Perusahaan</Button>
      </PageHeader>
      {!rows ? <Loading /> : (
        <div className="xl-wrap fade-up">
          <table className="xl" data-testid="tenant-table">
            <thead><tr><th className="rownum">#</th><th>Logo</th><th>Perusahaan</th><th>Kode Nota</th><th>Alamat</th><th>Kontak</th><th className="text-right">Nota/Hal</th><th className="text-right">Cadangan</th><th>Aktif</th><th>Aksi</th></tr></thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={t.id} data-testid={`tenant-row-${t.name}`}>
                  <td className="rownum">{i + 1}</td>
                  <td>{t.logo ? <img src={t.logo} alt="" className="h-7 w-7 object-contain" /> : <div className="h-7 w-7 bg-emerald-100 text-emerald-800 text-[10px] font-bold flex items-center justify-center rounded-sm">{t.nota_code}</div>}</td>
                  <td className="font-semibold">{t.name}</td>
                  <td className="font-mono">{t.nota_code}{t.extra_code}</td>
                  <td className="text-xs max-w-[200px] truncate">{t.address}</td>
                  <td className="text-xs">{t.phone}</td>
                  <td className="num">{t.notas_per_page}</td>
                  <td className="num">{t.reserve_pct}%</td>
                  <td><Switch checked={t.active} onCheckedChange={(v) => toggle(t, v)} data-testid={`tenant-active-${t.name}`} /></td>
                  <td className="whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setDlg({ open: "edit", tenant: t })} data-testid={`tenant-edit-${t.name}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDlg({ open: "prices", tenant: t })} data-testid={`tenant-prices-${t.name}`}><Tags className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TenantDialog state={dlg} onClose={() => setDlg({ open: null, tenant: null })} onSaved={load} />
      <Dialog open={dlg.open === "prices"} onOpenChange={(o) => !o && setDlg({ open: null, tenant: null })}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Daftar Harga · {dlg.tenant?.name}</DialogTitle></DialogHeader>
          {dlg.tenant && <PriceManager apiBase={`/admin/tenants/${dlg.tenant.id}/prices`} canEdit />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
